import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { LANGUAGE_STORAGE_KEY } from './storage'
import en from './locales/en/shell.json'
import fr from './locales/fr/shell.json'
import de from './locales/de/shell.json'

// The UI locales the app supports. Adding one here (and shipping a catalog for it
// in each pilet) is all it takes to offer another language in the switcher.
export const SUPPORTED_LANGUAGES = ['en', 'fr', 'de'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]
export const DEFAULT_LANGUAGE: Language = 'en'

// The `use-persisted-state` bucket the LanguageSwitcher writes and this module reads
// back at boot. Defined in the shell's storage inventory (src/storage.ts) and
// re-exported here so useLanguage keeps importing it alongside the language types.
export { LANGUAGE_STORAGE_KEY }

/**
 * The shell's own namespace. Unlike a pilet's catalog — registered through
 * `registerTranslations` and fetched as a per-locale chunk — every locale of this
 * one is bundled into the shell's entry, all three at once.
 *
 * That is deliberate and it is what the startup error page needs: the visitor sees
 * that page precisely when the network did not answer, so a locale chunk fetched on
 * demand is the one thing that cannot be relied on there. Three small catalogs cost
 * a few hundred bytes; a page that apologises in raw i18next keys costs the visitor
 * the only sentence that would have told them what to do.
 */
export const SHELL_NS = 'shell'
const shellResources = {
  en: { [SHELL_NS]: en },
  fr: { [SHELL_NS]: fr },
  de: { [SHELL_NS]: de }
}

const isSupported = (value: unknown): value is Language =>
  typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)

// Resolve the initial locale BEFORE React mounts, so the first paint is already in
// the right language: the persisted choice wins (same bucket the switcher uses),
// then the browser preference, then the default.
function detectInitialLanguage (): Language {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
    if (stored != null) {
      const parsed = JSON.parse(stored)
      if (isSupported(parsed)) return parsed
    }
  } catch {
    // localStorage blocked or malformed value — fall through to the browser hint.
  }
  const fromBrowser = (window.navigator.language || '').split('-')[0]
  return isSupported(fromBrowser) ? fromBrowser : DEFAULT_LANGUAGE
}

// The single shared i18next instance. Like use-persisted-state and the spatial-
// navigation singleton, it MUST be one instance across the shell and every pilet
// (declared in the portal importmap): react-i18next resolves `useTranslation`
// against this default instance, and pilets add their catalogs to it in setup().
// A pilet that bundled its own copy would get a private, empty instance and every
// key would fall back to itself.
i18n
  .use(initReactI18next)
  .init({
    lng: detectInitialLanguage(),
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    resources: shellResources,
    // The shell owns exactly one namespace — its own chrome (see shellResources).
    // Pilets contribute theirs at runtime (addResourceBundle), so none of them is
    // named here and the shell owns no pilet's keys. `defaultNS` stays false: every
    // consumer, shell and pilet alike, names its namespace explicitly.
    ns: [SHELL_NS],
    defaultNS: false,
    interpolation: {
      // React escapes rendered values already; letting i18next escape too would
      // double-encode interpolated strings.
      escapeValue: false
    },
    react: {
      // Catalogs load lazily (a pilet's active-locale chunk is fetched on demand),
      // so there is nothing to suspend on synchronously — disable Suspense.
      useSuspense: false,
      // A lazily-loaded catalog arrives AFTER first paint: addResourceBundle fires
      // an 'added' store event. Bind to it so components re-render when their
      // namespace lands, not only on languageChanged — otherwise the first render
      // would keep showing raw keys until the next language switch.
      bindI18nStore: 'added removed'
    },
    // Make untranslated keys loud during development. `just dev` prints a warning
    // instead of silently rendering the key. Swap the handler for a no-op (or gate
    // it behind a build flag) before shipping if the console noise is unwanted.
    saveMissing: true,
    missingKeyHandler: (lngs, ns, key) =>
      console.warn(`[i18n] missing key "${ns}:${key}" for ${lngs.join(', ')}`)
  })

export default i18n
